/*  MUI  */
import { Box, Button } from '@mui/material';

/* tanstack table */
import { Table } from '@tanstack/react-table';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/* Internal Components */
import { ManagedTablePaginationV2 } from './ManagedTablePaginationV2';

/**
 * Props interface for ManagedTableFooterV2 component
 * @interface ManagedTableFooterV2Props
 * @property {boolean} disabled - Controls whether the footer elements are disabled/inactive
 * @property {JSX.Element} footer - Custom footer element to be rendered in the table footer
 * @property {Table<unknown>} table - Table instance from tanstack/react-table library containing table state and methods
 */
interface ManagedTableFooterV2Props<TData> {
    disabled: boolean | undefined;
    footer: JSX.Element | undefined;
    table: Table<TData>;
}

/**
 * ManagedTableFooterV2 Component
 * Renders a footer component for managed tables with pagination and filter controls
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.disabled - Whether the footer controls are disabled
 * @param {JSX.Element} props.footer - Custom footer element to render
 * @param {Table<unknown>} props.table - Table instance from tanstack/react-table
 * @returns {JSX.Element} Footer component with pagination and filter controls
 */
export const ManagedTableFooterV2 = <TData,>({
    disabled,
    footer,
    table
}: ManagedTableFooterV2Props<TData>): JSX.Element => {
    const hasFiltersFromTableState = table
        .getState()
        .columnFilters?.filter(
            (filter) =>
                filter.value != null &&
                Array.isArray(filter.value) &&
                filter.value.length > 0
        );

    const onlyShowPagination = !footer;

    const { t } = useTranslation();

    return (
        <Box
            display="flex"
            className="hidePrint"
            alignItems="center"
            justifyContent={onlyShowPagination ? 'flex-end' : 'space-between'}
            width="100%"
            sx={{ padding: '8px' }}
        >
            {footer}
            <Box
                display="flex"
                flex={1}
                gap="8px"
                alignItems="center"
                justifyContent="space-between"
            >
                <Box>
                    <Button
                        color="primary"
                        data-trackid="tableClearAllTableFiltersBtn"
                        disabled={!(hasFiltersFromTableState?.length > 0)}
                        // Hide the button if no columns have filters
                        hidden={table
                            .getAllColumns()
                            .every(({ getCanFilter }) => !getCanFilter())}
                        onClick={() => {
                            table.resetColumnFilters(true);
                        }}
                        size="small"
                        variant="outlined"
                        style={{ marginLeft: '12px' }}
                    >
                        {t('ManagedTable.clear_filters')}
                    </Button>
                </Box>
                <Box>
                    <ManagedTablePaginationV2 disabled={disabled} />
                </Box>
            </Box>
        </Box>
    );
};
